import type { TouchEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ImageProps } from 'components/common/Image';

// Minimum horizontal travel (px) between touchstart and touchend to count as a swipe rather than a tap.
export const SWIPE_THRESHOLD_PX = 50;

export type SwipeDirection = 'LEFT' | 'RIGHT';

interface UseCardSwipeOptions {
  total: number;
  allImages: ImageProps[];
  enabled: boolean;
  /**
   * Fired once per committed swipe (past SWIPE_THRESHOLD_PX).
   * `index` is the image the user swiped AWAY from, not the destination.
   */
  onSwipe?: (info: { direction: SwipeDirection; index: number }) => void;
}

interface CardSwipeHandlers {
  onTouchStart: (e: TouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: TouchEvent<HTMLElement>) => void;
}

interface CardSwipe {
  activeIndex: number;
  handlers: CardSwipeHandlers;
  didSwipeRef: { current: boolean };
}

/**
 * Horizontal swipe cursor for the mobile multi-image preview. A touch move past
 * `SWIPE_THRESHOLD_PX` advances (left) or retreats (right) the angle index, wrapping both
 * ends, and sets `didSwipeRef` so the card can suppress the tap-to-navigate. Resets to MAIN
 * on image-set change (swatch swap); inert unless `enabled` (touch-only eligible card).
 */
export const useCardSwipe = ({ total, allImages, enabled, onSwipe }: UseCardSwipeOptions): CardSwipe => {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const didSwipeRef = useRef(false);

  // Snap back to MAIN whenever the image set changes (e.g. a swatch swap).
  useEffect(() => {
    setActiveIndex(0);
  }, [allImages]);

  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    if (!enabled) {
      return;
    }
    // Reset on every touchstart so a swipe-with-no-click can't swallow the next tap.
    didSwipeRef.current = false;
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: TouchEvent<HTMLElement>) => {
    if (!enabled) {
      return;
    }
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null || total <= 1) {
      return;
    }
    const endX = e.changedTouches[0]?.clientX;
    if (endX === undefined) {
      return;
    }
    const dx = endX - start;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) {
      return; // tap, not a swipe — leave didSwipeRef false so navigation proceeds
    }
    didSwipeRef.current = true; // a real swipe → suppress the click

    const direction: SwipeDirection = dx < 0 ? 'LEFT' : 'RIGHT';
    // `activeIndex` here is the image currently on screen — the one being swiped AWAY from.
    onSwipe?.({ direction, index: activeIndex });

    // Left swipe (dx < 0) → next angle; right swipe (dx > 0) → previous. Wrap.
    setActiveIndex(i => (dx < 0 ? (i + 1) % total : (i - 1 + total) % total));
  };

  return { activeIndex, handlers: { onTouchStart, onTouchEnd }, didSwipeRef };
};
